import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bell,
  Copy,
  Database,
  GitBranch,
  Inbox,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Rocket,
  Search,
  Settings,
  Trash2,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Logo } from "@/app/Logo";
import { useTheme, type ThemePref } from "@/app/theme";
import {
  ActorChip,
  AIBlock,
  AILabel,
  AbsoluteTime,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CodeBlock,
  ConfirmTyped,
  DataList,
  Dialog,
  DialogContent,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  ExplainPanel,
  Field,
  GradeBadge,
  IconButton,
  InlineCode,
  Input,
  Kbd,
  Meter,
  Money,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RelativeTime,
  SecretInput,
  Segmented,
  Select,
  Sheet,
  SheetContent,
  SheetTrigger,
  ShowAsCode,
  Skeleton,
  SkeletonText,
  Sparkline,
  Stat,
  StatusBadge,
  StatusDot,
  statusTables,
  Switch,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TBody,
  TD,
  TH,
  THead,
  Textarea,
  toast,
  Tooltip,
  TR,
  type StatusKind,
} from "@/design";
import type { Actor, Problem, SafetyGrade } from "@/lib/api/types";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/design")({
  staticData: { title: "Design system" },
  component: DesignPage,
});

const sections = [
  ["foundations", "Foundations"],
  ["colour", "Colour"],
  ["type", "Type"],
  ["buttons", "Buttons"],
  ["forms", "Forms"],
  ["navigation", "Tabs and segments"],
  ["status", "Status"],
  ["data", "Cards and numbers"],
  ["time-money", "Money and time"],
  ["code", "Code"],
  ["feedback", "Feedback"],
  ["explain", "Explain and AI"],
  ["safety", "Safety grades"],
  ["overlays", "Overlays"],
  ["danger", "Dangerous actions"],
  ["tables", "Tables and lists"],
] as const;

function DesignPage() {
  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-30 border-b border-border bg-canvas/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[1320px] items-center gap-4 px-4 sm:px-6 lg:px-10">
          <Link to="/" className="rounded-md">
            <Logo />
          </Link>
          <span className="hidden text-sm text-subtle sm:inline">Design system</span>
          <div className="ml-auto">
            <ThemeControls />
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-[1320px] gap-12 px-4 sm:px-6 lg:px-10">
        <nav className="sticky top-14 hidden h-[calc(100dvh-3.5rem)] w-48 shrink-0 overflow-y-auto py-10 lg:block" aria-label="Sections">
          <ul className="space-y-0.5">
            {sections.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="block rounded-md px-2.5 py-1.5 text-sm text-muted hover:bg-surface-2 hover:text-text">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <main className="min-w-0 flex-1 py-10 pb-32">
          <Intro />
          <Foundations />
          <Colour />
          <TypeScale />
          <Buttons />
          <Forms />
          <Navigation />
          <Status />
          <DataDisplay />
          <TimeMoney />
          <CodeSection />
          <FeedbackSection />
          <ExplainSection />
          <SafetySection />
          <Overlays />
          <DangerSection />
          <Tables />
        </main>
      </div>
    </div>
  );
}

function ThemeControls() {
  const { theme, setTheme, contrast, setContrast } = useTheme();
  return (
    <div className="flex items-center gap-3">
      <Segmented<ThemePref>
        aria-label="Theme"
        value={theme}
        onValueChange={setTheme}
        options={[
          { value: "light", label: "Light" },
          { value: "dark", label: "Dark" },
          { value: "system", label: "Auto" },
        ]}
      />
      <Switch size="sm" aria-label="High contrast" checked={contrast === "high"} onCheckedChange={(c) => setContrast(c ? "high" : "normal")} />
      <span className="hidden text-xs text-muted sm:inline">High contrast</span>
    </div>
  );
}

function Intro() {
  return (
    <div className="mb-16 max-w-3xl">
      <div className="text-sm font-medium text-accent-text">web/src/design</div>
      <h1 className="mt-2 font-display text-5xl font-semibold tracking-tight text-text">Calm, plain, trustworthy.</h1>
      <p className="mt-5 text-lg leading-relaxed text-muted">
        Guv looks after people's apps, often people who are not engineers. The interface should feel like a good British institution: quiet
        confidence, generous space, one clear accent in racing green, and every number explained in plain English. Import everything from{" "}
        <InlineCode>@/design</InlineCode>.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        <Badge variant="outline">WCAG 2.2 AA</Badge>
        <Badge variant="outline">Light, dark, high contrast</Badge>
        <Badge variant="outline">Keyboard first</Badge>
        <Badge variant="outline">British English</Badge>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- helpers */

function Section({ id, title, description, children }: { id: string; title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20 border-t border-border py-14 first-of-type:border-t-0">
      <h2 className="font-display text-3xl font-semibold tracking-tight text-text">{title}</h2>
      {description && <p className="mt-2 max-w-2xl text-md text-muted">{description}</p>}
      <div className="mt-8 space-y-10">{children}</div>
    </section>
  );
}

function Specimen({ title, note, code, children, className }: { title: string; note?: ReactNode; code?: string; children: ReactNode; className?: string }) {
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h3 className="text-md font-semibold text-text">{title}</h3>
        {note && <p className="text-sm text-muted">{note}</p>}
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
        <div className={cn("flex flex-wrap items-center gap-3 p-6", className)}>{children}</div>
        {code && (
          <div className="border-t border-border">
            <CodeBlock code={code} className="rounded-none border-0" />
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- foundations */

function Foundations() {
  return (
    <Section id="foundations" title="Foundations" description="Tokens live as CSS variables in src/styles/index.css and are mapped to Tailwind utilities. Components never use raw colours.">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Radius", "xs 4 · sm 6 · md 8 · lg 12 · xl 16", <div key="r" className="flex items-end gap-2">{["rounded-xs", "rounded-sm", "rounded-md", "rounded-lg", "rounded-xl"].map((r) => <div key={r} className={cn("size-8 border border-border-strong bg-surface-2", r)} />)}</div>],
          ["Elevation", "xs · sm · md · lg", <div key="e" className="flex items-end gap-3">{["shadow-xs", "shadow-sm", "shadow-md", "shadow-lg"].map((s) => <div key={s} className={cn("size-9 rounded-lg bg-surface", s)} />)}</div>],
          ["Spacing", "4px grid; pages breathe at 32–40px", <div key="s" className="flex items-end gap-1.5">{[1, 2, 3, 4, 6, 8, 10].map((n) => <div key={n} className="w-3 rounded-sm bg-accent/70" style={{ height: n * 4 }} />)}</div>],
          ["Motion", "160–260 ms, ease-out; off for reduced motion", <div key="m" className="flex items-center gap-2"><span className="size-3 animate-pulse-ring rounded-full bg-ok text-ok" /><span className="text-sm text-muted">Live</span></div>],
        ].map(([t, d, viz]) => (
          <Card key={t as string} padded>
            <div className="text-base font-semibold">{t}</div>
            <div className="mt-0.5 text-sm text-muted">{d}</div>
            <div className="mt-5 flex h-12 items-end">{viz}</div>
          </Card>
        ))}
      </div>
    </Section>
  );
}

const swatchGroups: [string, [string, string][]][] = [
  ["Surfaces", [["canvas", "bg-canvas"], ["sidebar", "bg-sidebar"], ["surface", "bg-surface"], ["surface-2", "bg-surface-2"], ["surface-3", "bg-surface-3"], ["border", "bg-border"], ["border-strong", "bg-border-strong"]]],
  ["Ink", [["text", "bg-text"], ["muted", "bg-muted"], ["subtle", "bg-subtle"]]],
  ["Accent", [["accent", "bg-accent"], ["accent-hover", "bg-accent-hover"], ["accent-soft", "bg-accent-soft"], ["focus", "bg-focus"]]],
  ["Status", [["ok", "bg-ok"], ["warn", "bg-warn"], ["danger", "bg-danger"], ["info", "bg-info"], ["idle", "bg-idle"], ["ai", "bg-ai"]]],
  ["Charts", [["chart-1", "bg-chart-1"], ["chart-2", "bg-chart-2"], ["chart-3", "bg-chart-3"], ["chart-4", "bg-chart-4"], ["chart-5", "bg-chart-5"]]],
];

function Colour() {
  return (
    <Section id="colour" title="Colour" description="Warm paper surfaces, navy-black ink and one confident accent: British racing green. Status colours are reserved for status. AI-written content is quietly violet so it is never mistaken for platform fact.">
      {swatchGroups.map(([group, sw]) => (
        <div key={group}>
          <h3 className="mb-3 text-md font-semibold">{group}</h3>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {sw.map(([name, cls]) => (
              <div key={name}>
                <div className={cn("h-16 rounded-lg border border-border shadow-xs", cls)} />
                <div className="mt-1.5 font-mono text-xs text-muted">{name}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </Section>
  );
}

function TypeScale() {
  return (
    <Section id="type" title="Type" description="Source Serif 4 for page titles and headline numbers gives a measured, editorial voice. Inter carries the interface at a 14px base. JetBrains Mono for code, ids and commands.">
      <Card>
        <div className="divide-y divide-border">
          {[
            ["5xl · display", "font-display text-5xl font-semibold", "£7.42 of £10"],
            ["3xl · display", "font-display text-3xl font-semibold", "Safety Centre"],
            ["xl · sans 600", "text-xl font-semibold", "Your app is live"],
            ["lg · sans 600", "text-lg font-semibold", "Deploy web to production"],
            ["md · sans", "text-md", "We'll build a Next.js app with Node 22 and give you a Postgres database."],
            ["base · sans", "text-base", "Backups on, last restore test passed 3 hours ago."],
            ["sm · muted", "text-sm text-muted", "Last checked 2 minutes ago"],
            ["xs · subtle", "text-xs text-subtle", "22 Oct 2026, 14:05 BST"],
            ["mono", "font-mono text-sm", "guv deploy --service web --env production"],
          ].map(([label, cls, sample]) => (
            <div key={label} className="grid gap-2 px-6 py-4 sm:grid-cols-[160px_1fr] sm:items-baseline">
              <div className="font-mono text-xs text-subtle">{label}</div>
              <div className={cn("truncate text-text", cls)}>{sample}</div>
            </div>
          ))}
        </div>
      </Card>
    </Section>
  );
}

/* --------------------------------------------------------------- components */

function Buttons() {
  const [loading, setLoading] = useState(false);
  return (
    <Section id="buttons" title="Buttons" description="One primary action per view. Danger only inside confirmations. Every icon-only button has a label and tooltip.">
      <Specimen title="Variants" code={`<Button variant="primary" icon={<Rocket />}>Deploy</Button>\n<Button>Cancel</Button>\n<Button variant="ghost">More</Button>`}>
        <Button variant="primary" icon={<Rocket />}>
          Deploy
        </Button>
        <Button icon={<RotateCcw />}>Roll back</Button>
        <Button variant="subtle">Subtle</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="danger" icon={<Trash2 />}>
          Delete database
        </Button>
        <Button variant="danger-ghost">Remove</Button>
        <Button variant="link" iconRight={<ArrowRight />}>
          View logs
        </Button>
      </Specimen>
      <Specimen title="Sizes and states" note="lg suits touch (44px) on the approval page.">
        <Button size="xs">Extra small</Button>
        <Button size="sm">Small</Button>
        <Button size="md">Medium</Button>
        <Button size="lg" variant="primary">
          Large
        </Button>
        <Button
          variant="primary"
          loading={loading}
          onClick={() => {
            setLoading(true);
            setTimeout(() => setLoading(false), 1600);
          }}
        >
          {loading ? "Deploying…" : "Click to load"}
        </Button>
        <Button disabled>Disabled</Button>
      </Specimen>
      <Specimen title="Icon buttons" code={`<IconButton label="Copy URL"><Copy /></IconButton>`}>
        <IconButton label="Search">
          <Search />
        </IconButton>
        <IconButton label="Notifications">
          <Bell />
        </IconButton>
        <IconButton label="Copy URL" variant="secondary">
          <Copy />
        </IconButton>
        <IconButton label="Edit" size="sm">
          <Pencil />
        </IconButton>
        <IconButton label="Settings" size="lg" variant="subtle">
          <Settings />
        </IconButton>
      </Specimen>
    </Section>
  );
}

function Forms() {
  const [region, setRegion] = useState("lon1");
  return (
    <Section id="forms" title="Forms" description="Labels above controls, hints below, errors replace hints. Secrets use SecretInput: write-only, never shown again after saving.">
      <Card padded>
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Project name" hint="Lowercase letters, numbers and dashes. You can change it later.">
            <Input placeholder="recipe-box" defaultValue="recipe-box" />
          </Field>
          <Field label="Custom domain" error="Use a hostname like shop.example.co.uk, without https://.">
            <Input defaultValue="https://shop.example" />
          </Field>
          <Field label="STRIPE_SECRET_KEY" hint="Stored encrypted. Not even we can read it back." aside="Secret">
            <SecretInput placeholder="sk_live_…" />
          </Field>
          <Field label="Region" hint="All data stays in the UK.">
            <Select
              value={region}
              onValueChange={setRegion}
              options={[
                { value: "lon1", label: "London", description: "OVHcloud, Erith data centre" },
                { value: "man1", label: "Manchester", description: "Coming 2027", disabled: true },
              ]}
            />
          </Field>
          <Field label="Search" hint="Inputs can carry a leading icon.">
            <Input leading={<Search />} placeholder="Search logs…" />
          </Field>
          <Field label="Notes for approver" aside="Optional">
            <Textarea placeholder="Why does the agent need this?" />
          </Field>
        </div>
        <div className="mt-8 max-w-md space-y-5 border-t border-border pt-6">
          <Switch label="Sleep when idle" description="Pause after 30 minutes without traffic. Wakes in about a second." defaultChecked />
          <Switch label="Pull request previews" description="Spin up an environment for every pull request." />
        </div>
      </Card>
    </Section>
  );
}

function Navigation() {
  const [range, setRange] = useState<"1h" | "6h" | "24h" | "7d">("6h");
  return (
    <Section id="navigation" title="Tabs and segments" description="Tabs switch content in place. RouteTabs (from @/app) change the URL. Segmented controls pick a value such as a time range.">
      <Specimen title="Tabs" className="block">
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="deployments">Deployments</TabsTrigger>
            <TabsTrigger value="variables">Variables</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="text-muted">Underline tabs for page sections.</TabsContent>
          <TabsContent value="deployments" className="text-muted">Deployments content.</TabsContent>
          <TabsContent value="variables" className="text-muted">Variables content.</TabsContent>
          <TabsContent value="settings" className="text-muted">Settings content.</TabsContent>
        </Tabs>
        <Tabs defaultValue="cli" className="mt-8">
          <TabsList variant="pill" className="inline-flex">
            <TabsTrigger value="cli">CLI</TabsTrigger>
            <TabsTrigger value="yaml">guv.yaml</TabsTrigger>
            <TabsTrigger value="api">API</TabsTrigger>
          </TabsList>
        </Tabs>
      </Specimen>
      <Specimen title="Segmented" code={`<Segmented aria-label="Range" value={range} onValueChange={setRange} options={[…]} />`}>
        <Segmented aria-label="Time range" value={range} onValueChange={setRange} options={(["1h", "6h", "24h", "7d"] as const).map((v) => ({ value: v, label: v }))} />
        <Segmented
          size="md"
          aria-label="View"
          value={range}
          onValueChange={setRange}
          options={[
            { value: "1h", label: "Live" },
            { value: "6h", label: "Today" },
            { value: "24h", label: "This week" },
          ]}
        />
      </Specimen>
    </Section>
  );
}

function Status() {
  const kinds: StatusKind[] = ["service", "deployment", "domain", "database", "approval"];
  return (
    <Section id="status" title="Status" description="Every status comes from one vocabulary (src/design/status.ts) with a plain-English tooltip. Hover or focus a dot to read it. No Kubernetes words, ever.">
      <Specimen title="StatusDot" code={`<StatusDot tone="ok" label="Live" explain="Your service is running and answering requests." />`}>
        <StatusDot tone="ok" label="Live" explain="Your service is running and answering requests." />
        <StatusDot tone="info" pulse label="Deploying" explain="The new version is starting." />
        <StatusDot tone="warn" label="Needs attention" explain="Something is not quite right." />
        <StatusDot tone="danger" label="Crashed" explain="Your app stopped unexpectedly." />
        <StatusDot tone="idle" label="Sleeping" explain="Paused while idle." />
      </Specimen>
      {kinds.map((k) => (
        <Specimen key={k} title={`StatusBadge kind="${k}"`} note="dot · pill">
          <div className="grid w-full gap-x-8 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
            {Object.keys(statusTables[k]).map((s) => (
              <div key={s} className="flex items-center justify-between gap-2">
                <StatusBadge kind={k} status={s} />
                <StatusBadge kind={k} status={s} variant="pill" />
              </div>
            ))}
          </div>
        </Specimen>
      ))}
      <Specimen title="Badge" code={`<Badge tone="ok">Live</Badge>  <Badge variant="outline">staging</Badge>  <Badge variant="dot" tone="warn">Waiting</Badge>`}>
        {(["neutral", "accent", "ok", "warn", "danger", "info", "ai"] as const).map((t) => (
          <Badge key={t} tone={t}>
            {t}
          </Badge>
        ))}
        <Badge variant="outline">production</Badge>
        <Badge variant="solid" tone="danger">
          Blocks deploy
        </Badge>
        <Badge variant="dot" tone="warn">
          Waiting for DNS
        </Badge>
        <Badge size="sm" tone="accent">
          Protected
        </Badge>
      </Specimen>
    </Section>
  );
}

const cpu = [0.12, 0.14, 0.13, 0.18, 0.22, 0.2, 0.31, 0.28, 0.26, 0.34, 0.3, 0.27, 0.29, 0.35, 0.41, 0.38, 0.33, 0.3, 0.32, 0.36, 0.34, 0.31, 0.29, 0.34];

function DataDisplay() {
  return (
    <Section id="data" title="Cards and numbers" description="Cards are quiet: a hairline border and the faintest shadow. Headline numbers use the display serif with tabular figures.">
      <div className="grid gap-4 md:grid-cols-3">
        <Card padded>
          <Stat
            label="This month"
            value={<Money value={7.42} />}
            hint={<>of your <Money value={10} whole /> cap</>}
            explain={
              <ExplainPanel title="What is a spend cap?">
                <p>A hard limit on what this project can cost in a month. At the cap we pause it rather than surprise you with a bill.</p>
              </ExplainPanel>
            }
            chart={<Meter value={7.42} max={10} forecast={9.85} label="Spend against cap" />}
          />
        </Card>
        <Card padded>
          <Stat label="CPU (last hour)" value="0.34" hint="vCPU on average, limit 2" delta={{ value: "12%", direction: "up" }} chart={<Sparkline data={cpu} width={240} height={40} label="CPU over the last hour, peak 0.41 vCPU" />} />
        </Card>
        <Card padded>
          <Stat label="Requests" value="18/s" hint="p95 latency 142 ms" delta={{ value: "4%", direction: "down", goodWhenUp: true }} chart={<Sparkline data={cpu.map((v) => v * 50 + Math.sin(v * 40) * 3)} width={240} height={40} color="var(--chart-2)" label="Requests per second" />} />
        </Card>
      </div>
      <Card>
        <CardHeader
          icon={<Database />}
          title="main"
          description="Postgres 18 · High availability · London"
          actions={
            <>
              <StatusBadge kind="database" status="available" variant="pill" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton label="More actions" size="sm">
                    <MoreHorizontal />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem icon={<GitBranch />}>Branch database</DropdownMenuItem>
                  <DropdownMenuItem icon={<RotateCcw />}>Restore…</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem icon={<Trash2 />} tone="danger">
                    Delete…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />
        <CardBody>
          <DataList
            layout="stack"
            items={[
              { term: "Storage", value: "18.4 GB of 50 GB" },
              { term: "Backups", value: "Last restore test passed 3 h ago" },
              { term: "Point-in-time restore", value: "14 days" },
              { term: "Connections", value: <InlineCode>main.guv.internal:5432</InlineCode> },
              { term: "Masking on branches", value: "4 rules" },
              { term: "Cost today", value: <Money value={0.42} /> },
            ]}
          />
        </CardBody>
        <CardFooter>Managed by guv.yaml. Changes here are shown as a diff before they apply.</CardFooter>
      </Card>
    </Section>
  );
}

function TimeMoney() {
  const now = Date.now();
  return (
    <Section id="time-money" title="Money and time" description="Pounds sterling, ex VAT, tabular figures. Dates as 22 Oct 2026, 14:05 BST; relative times update live with the absolute time on hover.">
      <Specimen title="Money" code={`<Money value={{ amount: "7.42", currency: "GBP" }} />  <Money value={10} whole />  <Money value={0.0042} precise suffix="/GB" />`}>
        <span className="font-display text-3xl font-semibold"><Money value={{ amount: "7.42", currency: "GBP" }} /></span>
        <Money value={10} whole />
        <Money value={1234.5} />
        <Money value={0.0042} precise suffix="/GB" />
        <Money value={20} whole suffix="/mo" />
      </Specimen>
      <Specimen title="RelativeTime and AbsoluteTime">
        <RelativeTime value={now - 20_000} />
        <RelativeTime value={now - 4 * 60_000} />
        <RelativeTime value={now - 3 * 3600_000} />
        <RelativeTime value={now + 2 * 86400_000} />
        <span className="text-subtle">·</span>
        <AbsoluteTime value="2026-10-22T13:05:00Z" />
        <AbsoluteTime value="2026-12-01T09:30:00Z" />
      </Specimen>
    </Section>
  );
}

function CodeSection() {
  return (
    <Section id="code" title="Code" description="Commands, manifests and evidence. Every block has a copy button. Shell prompts are not copied.">
      <div className="grid gap-4 lg:grid-cols-2">
        <CodeBlock title="Terminal" language="shell" code={"# Deploy the current directory\nguv up\nguv logs -f --service web --since 10m"} />
        <CodeBlock title="guv.yaml" language="yaml" code={"version: 1\nservices:\n  web:\n    build: railpack\n    port: 3000\ndatabases:\n  main:\n    engine: postgres"} />
        <CodeBlock title="guv.yaml (diff)" language="diff" code={"@@ services.web @@\n   web:\n-    port: 3000\n+    port: 8080\n     build: railpack"} />
        <div className="space-y-3 text-base text-text">
          <p>
            Inline: set <InlineCode>DATABASE_URL</InlineCode> to <InlineCode>{"${{ main.url }}"}</InlineCode>.
          </p>
          <p className="flex items-center gap-2">
            Press <Kbd mod>K</Kbd> to search, <Kbd>G</Kbd> <Kbd>L</Kbd> for logs, <Kbd>?</Kbd> for help.
          </p>
        </div>
      </div>
    </Section>
  );
}

const exampleProblem: Problem = {
  type: "https://guv.dev/errors/secret_missing",
  title: "A secret this needs has not been set",
  status: 422,
  code: "secret_missing",
  detail: "web references STRIPE_SECRET_KEY, but it is not set in production.",
  fix: "guv secrets set STRIPE_SECRET_KEY --env production",
  docs_url: "https://guv.dev/errors/secret_missing",
  request_id: "req_01jbx2m9k7q4r8t5",
};

function FeedbackSection() {
  return (
    <Section id="feedback" title="Feedback" description="Say what happened, why, and what to do next. Errors carry the stable code, a fix and a docs link. Never a stack trace.">
      <Specimen title="Callout: the failure banner" className="block space-y-3">
        <Callout
          tone="danger"
          title="Your app listens on port 8080 but we expected 3000."
          actions={
            <>
              <Button size="sm" variant="primary">
                Set port: 8080
              </Button>
              <Button size="sm">Ask AI to fix</Button>
            </>
          }
        >
          Your previous version is still serving, so nobody saw an outage.
        </Callout>
        <Callout tone="warn" title="You've used 84% of this month's cap.">
          At this rate you'll reach about £56.65. Non-production apps will sleep at £50.
        </Callout>
        <Callout tone="ok" title="Backups on and tested.">Last restore test passed 3 hours ago.</Callout>
        <Callout tone="info">Preview environments are deleted 72 hours after their pull request closes.</Callout>
      </Specimen>
      <div className="grid gap-4 lg:grid-cols-2">
        <ErrorState error={exampleProblem} onRetry={() => toast.info("Retrying…")} />
        <Card>
          <EmptyState
            icon={<Inbox />}
            title="No approvals waiting"
            description="When an agent asks to do something risky, like deploying to production, it appears here and on your phone."
            actions={<Button icon={<Plus />}>Create an agent token</Button>}
          />
        </Card>
      </div>
      <Specimen title="Toasts" code={`toast.success("Deploy started", { description: "We'll tell you when it's live." })\ntoast.error(apiError)`}>
        <Button onClick={() => toast.success("Deploy started", { description: "We'll tell you when web is live." })}>Success</Button>
        <Button onClick={() => toast.info("Copied to clipboard")}>Info</Button>
        <Button onClick={() => toast.warning("Spend is at 80% of your cap")}>Warning</Button>
        <Button onClick={() => toast.error(exampleProblem)}>Error from problem+json</Button>
        <Button onClick={() => toast({ title: "Rolled back web", description: "Now serving 9be1e07.", action: { label: "Undo", onClick: () => toast.info("Undone") } })}>With action</Button>
      </Specimen>
      <Specimen title="Skeleton" className="block">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex-1">
            <SkeletonText lines={2} />
          </div>
          <Skeleton className="h-8 w-24" />
        </div>
      </Specimen>
    </Section>
  );
}

function ExplainSection() {
  return (
    <Section id="explain" title="Explain and AI" description="Every status, error and number has a “What does this mean?”. Anything an AI wrote carries an AI label and asks if it helped. “Show as code” proves the UI is a window on the API.">
      <Specimen title="ExplainPanel">
        <span className="inline-flex items-center gap-1.5 text-base">
          Point-in-time restore
          <ExplainPanel title="What is point-in-time restore?" learnMoreUrl="https://guv.dev/docs/databases/restore" ask={{ question: "Should I turn on point-in-time restore?" }}>
            <p>We keep a continuous record of changes to your database, so you can rewind it to <strong>any second</strong> in the last 14 days.</p>
            <p>Useful when someone deletes the wrong rows at 14:03 and you notice at 16:00.</p>
          </ExplainPanel>
        </span>
        <ExplainPanel trigger="link" title="Why is my service sleeping?" ask={{ question: "Why is my service sleeping?" }}>
          <p>It had no traffic for 30 minutes, so we paused it. You pay nothing while it sleeps. The next visitor wakes it in about a second.</p>
        </ExplainPanel>
      </Specimen>
      <Specimen title="AI label" code={`<AILabel onFeedback={(helpful) => …} />`}>
        <AILabel />
        <AILabel feedback={false} />
      </Specimen>
      <AIBlock title="Why your deploy was blocked">
        Your <InlineCode>SUPABASE_SERVICE_ROLE_KEY</InlineCode> ended up in the JavaScript we send to browsers, so anyone visiting your site could read it.
        Move the code that needs it to a server function and rotate the key.
      </AIBlock>
      <Specimen title="ShowAsCode" note="Opens a side panel with CLI, guv.yaml diff and raw API call.">
        <ShowAsCode
          title="Change web's port to 8080"
          cli={"guv services update web --port 8080\nguv deploy --service web --env production"}
          manifestDiff={"   web:\n-    port: 3000\n+    port: 8080"}
          apiCall={{ method: "PATCH", path: "/v1/projects/rankctl/services/web", body: { port: 8080 } }}
        />
      </Specimen>
    </Section>
  );
}

function SafetySection() {
  const grades: SafetyGrade[] = ["A", "B", "C", "D", "E"];
  return (
    <Section id="safety" title="Safety grades" description="A to E, with a plain-English tooltip. A is racing green; E is the only red letter on the page.">
      <Specimen title="GradeBadge" code={`<GradeBadge grade="A" size="lg" showLabel />`}>
        {grades.map((g) => (
          <GradeBadge key={g} grade={g} size="lg" showLabel />
        ))}
      </Specimen>
      <Specimen title="Sizes">
        <GradeBadge grade="A" size="sm" />
        <GradeBadge grade="A" size="md" />
        <GradeBadge grade="A" size="lg" />
        <GradeBadge grade="A" size="xl" />
        <GradeBadge grade={null} size="lg" />
      </Specimen>
    </Section>
  );
}

function Overlays() {
  return (
    <Section id="overlays" title="Overlays" description="Dialogs for decisions, side panels to keep context, popovers for detail, tooltips for a few words.">
      <Specimen title="Dialog, Sheet, Popover, Tooltip, DropdownMenu">
        <Dialog>
          <DialogTrigger asChild>
            <Button>Open dialog</Button>
          </DialogTrigger>
          <DialogContent
            title="Add a domain"
            description="Point a domain you own at this service. We'll handle HTTPS."
            footer={
              <>
                <Button>Cancel</Button>
                <Button variant="primary">Continue</Button>
              </>
            }
          >
            <Field label="Domain" hint="For example shop.example.co.uk">
              <Input placeholder="shop.example.co.uk" />
            </Field>
          </DialogContent>
        </Dialog>
        <Sheet>
          <SheetTrigger asChild>
            <Button>Open side panel</Button>
          </SheetTrigger>
          <SheetContent title="web" description="Next.js 15 · production" leading={<StatusDot tone="ok" size="lg" />} size="lg">
            <DataList
              items={[
                { term: "Status", value: <StatusBadge kind="service" status="running" /> },
                { term: "Current deploy", value: <InlineCode>3f9a1c2</InlineCode> },
                { term: "URL", value: "app.primecarers.co.uk" },
                { term: "Cost today", value: <Money value={0.31} /> },
              ]}
            />
          </SheetContent>
        </Sheet>
        <Popover>
          <PopoverTrigger asChild>
            <Button>Open popover</Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="text-base font-semibold">DNS records</div>
            <p className="mt-1 text-sm text-muted">Add a CNAME for www pointing to web-primecarers.guv.run.</p>
          </PopoverContent>
        </Popover>
        <Tooltip content="Copies the live URL">
          <Button icon={<Copy />}>Hover me</Button>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button iconRight={<MoreHorizontal />}>Menu</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>web</DropdownMenuLabel>
            <DropdownMenuItem icon={<Rocket />} shortcut="D">
              Deploy
            </DropdownMenuItem>
            <DropdownMenuItem icon={<RotateCcw />}>Roll back</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem icon={<Trash2 />} tone="danger">
              Delete service
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Specimen>
    </Section>
  );
}

function DangerSection() {
  const [open, setOpen] = useState(false);
  return (
    <Section id="danger" title="Dangerous actions" description="Deleting data, restoring over production, disabling backups, exposing a database and raising spend caps always need a typed confirmation (docs/11 §4).">
      <Specimen title="ConfirmTyped" code={`<ConfirmTyped open={open} onOpenChange={setOpen} title="Delete database main?" confirmText="main" actionLabel="Delete database" consequences={[…]} onConfirm={…} />`}>
        <Button variant="danger" icon={<Trash2 />} onClick={() => setOpen(true)}>
          Delete database…
        </Button>
        <ConfirmTyped
          open={open}
          onOpenChange={setOpen}
          title="Delete database main?"
          confirmText="main"
          actionLabel="Delete database"
          consequences={[
            <>All data in <strong>main</strong> (18.4 GB) will be deleted.</>,
            "You can undo this for 7 days. After that it is gone for good, including backups.",
            "web and worker will stop working until you point them at another database.",
          ]}
          onConfirm={async () => {
            await new Promise((r) => setTimeout(r, 900));
            setOpen(false);
            toast.success("Database scheduled for deletion", { description: "Undo any time in the next 7 days." });
          }}
        />
      </Specimen>
    </Section>
  );
}

const sampleActors: Actor[] = [
  { type: "user", id: "usr_1", display: "Stewart Webb" },
  { type: "agent", id: "tok_1", display: "claude-code-on-stewart-laptop", on_behalf_of: { id: "usr_1", display: "Stewart Webb" } },
  { type: "github", id: "gh", display: "GitHub" },
  { type: "ai_ops", id: "ai", display: "Guv AI operations" },
];

function Tables() {
  return (
    <Section id="tables" title="Tables and lists" description="Dense but calm. Row hover only when the row is clickable. Actors are always attributed.">
      <Card>
        <Table>
          <THead>
            <tr>
              <TH>Service</TH>
              <TH>Status</TH>
              <TH>Who</TH>
              <TH className="text-right">Cost today</TH>
              <TH className="text-right">When</TH>
            </tr>
          </THead>
          <TBody>
            {[
              ["web", "active", sampleActors[0]!, 0.31, 14],
              ["web", "failed", sampleActors[2]!, 0, 47],
              ["api", "building", sampleActors[1]!, 0.12, 1],
              ["worker", "superseded", sampleActors[3]!, 0.05, 300],
            ].map(([svc, st, actor, cost, mins], i) => (
              <TR key={i} interactive>
                <TD className="font-medium">{svc as string}</TD>
                <TD>
                  <StatusBadge kind="deployment" status={st as string} />
                </TD>
                <TD>
                  <ActorChip actor={actor as Actor} size="sm" />
                </TD>
                <TD className="text-right">
                  <Money value={cost as number} />
                </TD>
                <TD className="text-right text-muted">
                  <RelativeTime value={Date.now() - (mins as number) * 60_000} />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
    </Section>
  );
}
